import React, { useEffect, useRef, useState } from 'react';
import { stepsData } from '../data/mock';
import { Lightbulb, Clapperboard, Rocket } from 'lucide-react';

const icons = [Lightbulb, Clapperboard, Rocket];

const StepsSection = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible) {
      const interval = setInterval(() => {
        setActiveStep((prev) => (prev + 1) % stepsData.length);
      }, 2500);
      return () => clearInterval(interval);
    }
  }, [isVisible]);

  return (
    <section ref={sectionRef} className="py-24 bg-[#F5FDFF] relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#00C4CC] via-[#00E08F] to-[#FFF7AE]" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2
            className={`text-3xl md:text-4xl font-bold text-[#04101C] mb-4 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            What We <span className="bg-gradient-to-r from-[#00C4CC] to-[#00E08F] bg-clip-text text-transparent">Do</span>
          </h2>
          <p
            className={`text-[#04101C]/60 max-w-xl mx-auto transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            From concept to global audience — simplified.
          </p>
        </div>

        {/* Progress Bar */}
        <div className="relative max-w-3xl mx-auto mb-12">
          <div className="h-2 bg-[#04101C]/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#00C4CC] via-[#00E08F] to-[#FFF7AE] rounded-full transition-all duration-500 ease-out"
              style={{ width: `${((activeStep + 1) / stepsData.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-8">
          {stepsData.map((step, index) => {
            const Icon = icons[index];
            const isActive = index === activeStep;
            const isPast = index < activeStep;

            return (
              <div
                key={step.id}
                className={`relative p-8 rounded-2xl transition-all duration-500 cursor-pointer ${
                  isActive
                    ? 'bg-gradient-to-br from-[#00C4CC]/10 to-[#00E08F]/10 border-2 border-[#00E08F] scale-105 shadow-xl'
                    : isPast
                    ? 'bg-white border-2 border-[#00C4CC]/30'
                    : 'bg-white border-2 border-transparent hover:border-[#00C4CC]/20'
                } ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
                onClick={() => setActiveStep(index)}
              >
                {/* Step Number */}
                <div
                  className={`absolute -top-4 -left-4 w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg transition-all duration-300 ${
                    isActive || isPast
                      ? 'bg-gradient-to-r from-[#00C4CC] to-[#00E08F] text-[#04101C]'
                      : 'bg-[#04101C]/10 text-[#04101C]/40'
                  }`}
                >
                  {step.id}
                </div>

                {/* Icon */}
                <div
                  className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-all duration-300 ${
                    isActive
                      ? 'bg-gradient-to-br from-[#00C4CC] to-[#00E08F]'
                      : 'bg-[#04101C]/5'
                  }`}
                >
                  <Icon
                    size={28}
                    className={`transition-colors duration-300 ${
                      isActive ? 'text-[#04101C]' : 'text-[#00C4CC]'
                    }`}
                  />
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold text-[#04101C] mb-2">{step.title}</h3>
                <p className="text-[#04101C]/60">{step.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default StepsSection;
